import type { HttpInterceptorFn } from '@angular/common/http';
import { inject, InjectionToken, type Provider } from '@angular/core';

// 이 토큰/provide 함수만 별도 lib으로 분리한 이유(2026-08-20) — schoolbell-e-web(popup 컨테이너)
// 은 member-picker 컴포넌트 lib을 `loadComponent()`로 지연 로드하면서, 동시에 이 토큰은
// app.config.ts에서 부트타임에 정적으로 provide해야 한다. 같은 lib을 한 앱 안에서
// 정적+지연 혼용하면 `@nx/enforce-module-boundaries`가 막는다
// (feedback_nx_lazy_lib_cannot_mix_static_import_same_app) — 무거운 컴포넌트/Material 의존이
// 없는 이 토큰만 별도 lib으로 빼서, 앱은 이 lib만 정적으로 import하고 컴포넌트 lib은 계속
// 지연 로드할 수 있게 한다.
export const MEMBER_PICKER_API_BASE_URL = new InjectionToken<string>('MEMBER_PICKER_API_BASE_URL');

export function provideMemberPickerApiBaseUrl(baseUrl: string): Provider {
  return { provide: MEMBER_PICKER_API_BASE_URL, useValue: baseUrl };
}

// orval이 생성한 member-picker 클라이언트(SchoolBellEAPIService)는 base URL 없이 상대 경로만
// 낸다. schoolbell-e-web/embed-web의 다른 서비스(ApiService, SpinoffAuthService)는 모두
// 호출부에서 직접 절대 URL을 조립하는 컨벤션이라 전역 interceptor가 없다 — 그 컨벤션을 깨지
// 않도록 이 interceptor는 무인증 경로(GET .../sessions/:code, POST .../sessions/:code/confirm)
// 에만 좁게 반응한다. createSession(POST .../sessions)·exchangeSession(POST
// .../sessions/:code/exchange)은 RP 백엔드 전용(org access token Bearer 인증)이라 일부러
// 제외한다 — 접두사만으로 매칭하면 이 둘도 함께 걸려, 나중에 이 서비스를 거치지 않고
// SchoolBellEAPIService를 직접 주입해 호출할 경우 인증 헤더 없이 조용히 401이 날 수 있다.
//
// 부수 효과: 예전엔 이 요청들이 절대 URL로 조립돼(baseUrl === SPINOFF_AUTH_CONFIG.apiBaseUrl)
// spinoffAuthInterceptor의 가드(`req.url.startsWith(config.apiBaseUrl)`)를 그대로 통과해,
// 로그인된 상태로 팝업을 열면 무인증 의도였던 이 요청에도 Bearer 토큰이 실제로 붙고 있었다.
// 이 interceptor가 상대경로를 spinoffAuthInterceptor 통과 이후에만 절대경로로 바꾸므로(app.config.ts
// 의 interceptor 순서 고정 주석 참고) 그 의도치 않은 부착이 사라진다 — 대신 infra-error 배너
// 연동(spinoffAuthInterceptor의 tap/catchError)도 함께 빠지는데, 원래 이 경로를 앱의 전역
// 클라이언트와 분리하려던 설계 의도(member-picker-api.service.ts 주석)와 일치하는 트레이드오프다.
const UNAUTHENTICATED_MEMBER_PICKER_PATH = /^\/oauth\/member-picker\/sessions\/[^/]+(?:\/confirm)?$/;

// @sbe-spinoff-client/staff-http에 동일한 normalizeBaseUrl이 이미 있지만 재사용하지 않는다 —
// 그 lib의 배럴(index.ts)이 Angular 데코레이터가 붙은 provider(staff-http.config.ts,
// staff-api-base-url.interceptor.ts)도 함께 재수출해서, schoolbell-e-web/embed-web처럼
// staff-http를 한 번도 직접 쓴 적 없는 앱까지 그 파일들을 TS 컴파일 대상에 끌어들인다 —
// tsconfig references가 전이적으로는 안 걸려 esbuild 기반 Angular 컴파일러가 "Angular
// metadata가 있는 파일은 컴파일 대상이어야 한다"는 하드 에러를 낸다(schoolbell-e-e2e CI에서
// 실측). 한 줄짜리 로직이라 중복 비용보다 이 쪽이 싸다.
function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
}

export const memberPickerApiBaseUrlInterceptor: HttpInterceptorFn = (req, next) => {
  const baseUrl = inject(MEMBER_PICKER_API_BASE_URL);
  if (!baseUrl || !UNAUTHENTICATED_MEMBER_PICKER_PATH.test(req.url)) {
    return next(req);
  }
  return next(req.clone({ url: `${normalizeBaseUrl(baseUrl)}${req.url}` }));
};
